<template>
  <div>
    <!-- Trigger Button -->
    <button
      v-if="!isOpen"
      @click="isOpen = true"
      class="px-4 py-2 bg-mumble-accent text-white text-sm font-semibold rounded-lg hover:bg-mumble-accent/90 transition-all duration-200 flex items-center gap-2 cursor-pointer shadow-md hover:shadow-lg animate-pulse-gentle"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
      <span>Aiutami a scegliere</span>
    </button>

    <!-- Quiz Modal -->
    <Transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        @click.self="isOpen = false"
      >
        <Transition name="slide-up">
          <div
            v-if="isOpen"
            class="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden"
          >
            <!-- Header -->
            <div class="p-6 border-b border-gray-100">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-semibold text-mumble-text">Scegli il piano giusto</h3>
                <button
                  @click="isOpen = false"
                  class="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>

              <!-- Progress -->
              <div v-if="!showResult" class="flex items-center gap-2">
                <div class="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    class="h-full bg-mumble-accent transition-all duration-500 ease-out"
                    :style="{ width: `${((currentStep + 1) / questions.length) * 100}%` }"
                  ></div>
                </div>
                <span class="text-xs text-gray-500 whitespace-nowrap">{{ currentStep + 1 }} / {{ questions.length }}</span>
              </div>
            </div>

            <!-- Content -->
            <div class="p-6 overflow-hidden">
              <!-- Questions -->
              <TransitionGroup name="question" tag="div" v-if="!showResult" class="relative">
                <div v-for="(question, index) in questions" :key="question.id" v-show="currentStep === index">
                  <p class="text-base font-medium text-mumble-text mb-6">{{ question.text }}</p>

                  <div class="space-y-3">
                    <button
                      v-for="(option, optIndex) in question.options"
                      :key="optIndex"
                      @click="selectAnswer(optIndex)"
                      class="w-full text-left p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer"
                      :class="[
                        answers[index] === optIndex
                          ? 'border-mumble-accent bg-mumble-accent/5'
                          : 'border-gray-100 hover:border-mumble-accent/30 hover:bg-gray-50'
                      ]"
                    >
                      <span class="text-sm text-gray-700">{{ option }}</span>
                    </button>
                  </div>
                </div>
              </TransitionGroup>

              <!-- Result -->
              <Transition name="fade">
                <div v-if="showResult" class="text-center py-4">
                  <div class="w-16 h-16 bg-mumble-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-8 h-8 text-mumble-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>

                  <p class="text-sm text-gray-600 mb-2">Il piano più adatto a te è</p>
                  <p class="text-2xl font-bold text-mumble-accent mb-6">{{ recommendedPackage?.displayName }}</p>

                  <div class="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                    <p class="text-xs text-gray-500 leading-relaxed">{{ packageDescription }}</p>
                  </div>

                  <div class="flex gap-3">
                    <button
                      @click="reset"
                      class="flex-1 px-4 py-3 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      Ricomincia
                    </button>
                    <button
                      @click="confirmSelection"
                      class="flex-1 px-4 py-3 bg-mumble-accent text-white text-sm font-medium rounded-xl hover:bg-mumble-accent/90 transition-colors cursor-pointer"
                    >
                      Seleziona piano
                    </button>
                  </div>
                </div>
              </Transition>
            </div>

            <!-- Footer Navigation -->
            <div v-if="!showResult" class="p-6 pt-0 flex justify-between">
              <button
                v-if="currentStep > 0"
                @click="currentStep--"
                class="px-4 py-2 text-sm text-gray-600 hover:text-mumble-text transition-colors cursor-pointer"
              >
                ← Indietro
              </button>
              <div v-else></div>

              <button
                v-if="answers[currentStep] !== null"
                @click="nextStep"
                class="px-6 py-2 bg-mumble-accent text-white text-sm font-medium rounded-lg hover:bg-mumble-accent/90 transition-colors cursor-pointer"
              >
                {{ currentStep === questions.length - 1 ? 'Vedi risultato' : 'Avanti →' }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, PropType } from 'vue';
import { track } from '@vercel/analytics';

interface Package {
  name: string;
  displayName: string;
  baseCost: number;
  hourlyRate: number;
  includedHours: number;
  responseTime: string;
}

// Helper to get device type
const getDeviceType = (): string => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Helper to get tracking metadata
const getTrackingMetadata = () => ({
  device_type: getDeviceType(),
  screen_width: window.innerWidth,
  screen_height: window.innerHeight,
  user_agent: navigator.userAgent,
  language: navigator.language,
  timestamp: new Date().toISOString()
});

export default defineComponent({
  name: 'QuizFinder',
  props: {
    packages: {
      type: Object as PropType<Record<string, Package>>,
      required: true
    }
  },
  emits: ['select-package'],
  setup(props, { emit }) {
    const isOpen = ref(false);
    const currentStep = ref(0);
    const showResult = ref(false);
    const answers = ref<(number | null)[]>([null, null, null, null, null]);

    // Track quiz open/close
    watch(isOpen, (open) => {
      if (open) {
        track('quiz_opened', {
          ...getTrackingMetadata()
        });
      } else if (!showResult.value) {
        // Only track close if not showing result (to avoid duplicate tracking)
        track('quiz_closed', {
          completed: false,
          current_step: currentStep.value,
          total_steps: questions.length,
          ...getTrackingMetadata()
        });
      }
    });

    const questions = [
      {
        id: 'phase',
        text: 'In che fase si trova il tuo prodotto?',
        options: [
          'È un progetto sperimentale, un MVP o qualcosa che non è ancora critico',
          'È in produzione e viene usato regolarmente, ma non è il cuore del business',
          'È un prodotto attivo che deve crescere e migliorare costantemente',
          'È mission-critical: se smette di funzionare, il business si ferma'
        ]
      },
      {
        id: 'urgency',
        text: 'Quando segnali un problema, quanto puoi aspettare prima di sapere che qualcuno lo sta gestendo?',
        options: [
          'Non ho fretta, basta che prima o poi qualcuno risponda',
          'Entro un paio di giorni lavorativi va bene',
          'Entro la giornata vorrei almeno una conferma di presa in carico',
          'Ho bisogno di sapere in poche ore che il team sta lavorando al problema'
        ]
      },
      {
        id: 'evolution',
        text: 'Con quale frequenza prevedi di richiedere modifiche o nuove funzionalità?',
        options: [
          'Raramente, solo interventi occasionali quando serve',
          'Qualche volta all\'anno, per piccole correzioni o aggiustamenti',
          'Regolarmente, il prodotto deve evolvere con il mercato',
          'Continuamente, abbiamo una roadmap attiva e rilasci frequenti'
        ]
      },
      {
        id: 'support',
        text: 'Quanto è importante per te avere un punto di riferimento dedicato?',
        options: [
          'Non mi serve, preferisco pagare solo quando ho bisogno',
          'Sarebbe utile per avere ordine, ma non è prioritario',
          'È importante: voglio qualcuno che conosca il progetto',
          'È essenziale: ho bisogno di una persona che gestisca proattivamente'
        ]
      },
      {
        id: 'security',
        text: 'Come ti senti riguardo alla sicurezza e agli aggiornamenti?',
        options: [
          'Me ne occupo solo se emerge un problema evidente',
          'Vorrei un monitoraggio base, ma non è la priorità',
          'Voglio essere avvisato di vulnerabilità e avere fix tempestivi',
          'La sicurezza è critica: mi aspetto scansioni continue'
        ]
      }
    ];

    const packageDescriptions: Record<string, string> = {
      none: 'Questo modello è pensato per interventi occasionali: non include manutenzione preventiva, aggiornamenti programmati, SLA o garanzie sulla stabilità.',
      essential: 'Questo piano copre la gestione di base, ma non è indicato per prodotti in crescita né per situazioni che richiedono interventi rapidi.',
      growth: 'Questo piano consente di far evolvere il prodotto, ma non include la gestione strutturata degli incidenti critici o SLA dedicati sul downtime.',
      full: 'Questo piano copre continuità, sicurezza ed evoluzione del prodotto. Sono escluse solo le major release.'
    };

    const recommendedPackage = computed(() => {
      const validAnswers = answers.value.filter(a => a !== null) as number[];
      if (validAnswers.length < questions.length) return null;

      // Se anche una sola risposta è critica (3), suggerisci Full Care
      const hasCriticalAnswer = validAnswers.some(a => a === 3);
      if (hasCriticalAnswer) return props.packages.full;

      const score = validAnswers.reduce((sum, val) => sum + val, 0) / validAnswers.length;

      // Soglie calibrate per favorire Growth e Full
      if (score < 0.5) return props.packages.none;
      if (score < 1.2) return props.packages.essential;
      return props.packages.growth;
    });

    const packageDescription = computed(() => {
      if (!recommendedPackage.value) return '';
      return packageDescriptions[recommendedPackage.value.name] || '';
    });

    const selectAnswer = (optionIndex: number) => {
      answers.value[currentStep.value] = optionIndex;

      // Track answer selection
      const question = questions[currentStep.value];
      track('quiz_answer_selected', {
        question_id: question.id,
        question_text: question.text,
        step: currentStep.value + 1,
        total_steps: questions.length,
        answer_index: optionIndex,
        answer_text: question.options[optionIndex],
        ...getTrackingMetadata()
      });
    };

    const nextStep = () => {
      if (currentStep.value < questions.length - 1) {
        currentStep.value++;
      } else {
        showResult.value = true;

        // Track quiz completion
        const pkg = recommendedPackage.value;
        if (pkg) {
          track('quiz_completed', {
            recommended_package: pkg.name,
            recommended_package_display: pkg.displayName,
            package_base_cost: pkg.baseCost,
            package_included_hours: pkg.includedHours,
            answers: JSON.stringify(answers.value),
            ...getTrackingMetadata()
          });
        }
      }
    };

    const reset = () => {
      track('quiz_restarted', {
        from_step: currentStep.value,
        had_recommendation: recommendedPackage.value !== null,
        previous_recommendation: recommendedPackage.value?.name || null,
        ...getTrackingMetadata()
      });

      currentStep.value = 0;
      showResult.value = false;
      answers.value = [null, null, null, null, null];
    };

    const confirmSelection = () => {
      if (recommendedPackage.value) {
        track('quiz_package_confirmed', {
          package_name: recommendedPackage.value.name,
          package_display_name: recommendedPackage.value.displayName,
          package_base_cost: recommendedPackage.value.baseCost,
          package_included_hours: recommendedPackage.value.includedHours,
          answers: JSON.stringify(answers.value),
          ...getTrackingMetadata()
        });

        emit('select-package', recommendedPackage.value);
        isOpen.value = false;
        reset();
      }
    };

    return {
      isOpen,
      currentStep,
      showResult,
      answers,
      questions,
      recommendedPackage,
      packageDescription,
      selectAnswer,
      nextStep,
      reset,
      confirmSelection
    };
  }
});
</script>

<style scoped>
/* Pulse animation for CTA button */
@keyframes pulse-gentle {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 4px 6px -1px rgba(242, 140, 115, 0.3), 0 2px 4px -1px rgba(242, 140, 115, 0.2);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 10px 15px -3px rgba(242, 140, 115, 0.4), 0 4px 6px -2px rgba(242, 140, 115, 0.3);
  }
}

.animate-pulse-gentle {
  animation: pulse-gentle 2s ease-in-out infinite;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.question-enter-active {
  transition: all 0.3s ease;
}

.question-leave-active {
  transition: all 0.2s ease;
  position: absolute;
}

.question-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.question-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}
</style>
